import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrendingUp, Calendar, MessageCircle, ClipboardCheck, BookOpen, Sparkles,
  Target, CheckCircle2, Circle, Trophy, ChevronRight, FileText, ArrowRight,
  Plus, Trash2, Clock
} from 'lucide-react';
import { studentApi, chatApi, assessmentApi, notebookApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function StudentDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [progress, setProgress] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [notebooks, setNotebooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Daily goals from localStorage
  const today = new Date().toISOString().split('T')[0];
  const [goals, setGoals] = useState(() => {
    const saved = localStorage.getItem(`goals-${today}`);
    if (saved) return JSON.parse(saved);
    return {
      mockTest: false,
      doubts: 0,
      notebook: false,
      studyMaterial: false,
    };
  });

  // Day planner from localStorage
  const [plannerTasks, setPlannerTasks] = useState(() => {
    const saved = localStorage.getItem(`planner-${today}`);
    if (saved) return JSON.parse(saved);
    return [];
  });
  const [newTask, setNewTask] = useState('');
  const [newTaskTime, setNewTaskTime] = useState('');

  const savePlanner = (tasks) => {
    setPlannerTasks(tasks);
    localStorage.setItem(`planner-${today}`, JSON.stringify(tasks));
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    const tasks = [...plannerTasks, { id: Date.now(), text: newTask.trim(), time: newTaskTime || null, done: false }];
    savePlanner(tasks);
    setNewTask('');
    setNewTaskTime('');
  };

  const toggleTask = (id) => {
    const tasks = plannerTasks.map(t => t.id === id ? { ...t, done: !t.done } : t);
    savePlanner(tasks);
  };

  const deleteTask = (id) => {
    savePlanner(plannerTasks.filter(t => t.id !== id));
  };

  useEffect(() => {
    loadData();
  }, []);

  // Auto-detect completed goals from data
  useEffect(() => {
    if (!progress || !attempts.length === undefined) return;
    const todayAttempts = attempts.filter(a => a.started_at?.startsWith(today));
    const todayDoubts = progress?.todayQuestions || 0;
    const todayNotebooks = notebooks.filter(n => n.created_at?.startsWith(today));

    const updated = {
      mockTest: todayAttempts.length > 0 || goals.mockTest,
      doubts: Math.max(todayDoubts, goals.doubts),
      notebook: todayNotebooks.length > 0 || goals.notebook,
      studyMaterial: goals.studyMaterial,
    };
    setGoals(updated);
    localStorage.setItem(`goals-${today}`, JSON.stringify(updated));
  }, [progress, attempts, notebooks]);

  const loadData = async () => {
    try {
      const [usageRes, progressRes, attemptsRes, notebooksRes] = await Promise.all([
        studentApi.getUsage().catch(() => ({ data: null })),
        studentApi.getProgress().catch(() => ({ data: null })),
        assessmentApi.getMyAttempts().catch(() => ({ data: [] })),
        notebookApi.list().catch(() => ({ data: [] })),
      ]);
      setUsage(usageRes.data);
      setProgress(progressRes.data);
      setAttempts(attemptsRes.data || []);
      setNotebooks(notebooksRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Compute stats
  const gradedAttempts = attempts.filter(a => a.status === 'graded' && a.score !== null && a.max_score > 0);
  const avgScore = gradedAttempts.length > 0
    ? Math.round(gradedAttempts.reduce((sum, a) => sum + (a.score / a.max_score) * 100, 0) / gradedAttempts.length)
    : null;
  const bestAttempt = gradedAttempts.length > 0
    ? gradedAttempts.reduce((best, a) => {
        const pct = (a.score / a.max_score) * 100;
        return pct > best.pct ? { ...a, pct } : best;
      }, { pct: 0 })
    : null;
  const totalQuestionsPracticed = gradedAttempts.reduce((sum, a) => sum + (a.max_score || 0), 0);

  // Daily goals progress
  const goalsCompleted = [goals.mockTest, goals.doubts >= 3, goals.notebook].filter(Boolean).length;
  const totalGoals = 3;
  const goalPercent = Math.round((goalsCompleted / totalGoals) * 100);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.name?.split(' ')[0]}!</h1>
        <p className="text-gray-500 text-sm mt-1">Here's your study progress for today</p>
      </div>

      {/* Daily Goals */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Target className="w-5 h-5 mr-2" />
            <h2 className="text-lg font-semibold">Today's Goals</h2>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold">{goalsCompleted}/{totalGoals}</span>
            <p className="text-xs text-indigo-200">completed</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-white/20 rounded-full h-2.5 mb-4">
          <div
            className="bg-white rounded-full h-2.5 transition-all duration-500"
            style={{ width: `${goalPercent}%` }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className={`flex items-center p-3 rounded-xl ${goals.mockTest ? 'bg-white/20' : 'bg-white/10'}`}>
            {goals.mockTest ? <CheckCircle2 className="w-5 h-5 mr-2 text-green-300" /> : <Circle className="w-5 h-5 mr-2 text-white/50" />}
            <div>
              <p className={`text-sm font-medium ${goals.mockTest ? 'line-through text-white/70' : ''}`}>Take a Mock Test</p>
              <p className="text-xs text-indigo-200">Complete 1 test today</p>
            </div>
          </div>

          <div className={`flex items-center p-3 rounded-xl ${goals.doubts >= 3 ? 'bg-white/20' : 'bg-white/10'}`}>
            {goals.doubts >= 3 ? <CheckCircle2 className="w-5 h-5 mr-2 text-green-300" /> : <Circle className="w-5 h-5 mr-2 text-white/50" />}
            <div>
              <p className={`text-sm font-medium ${goals.doubts >= 3 ? 'line-through text-white/70' : ''}`}>Ask 3 Doubts</p>
              <p className="text-xs text-indigo-200">{goals.doubts}/3 asked today</p>
            </div>
          </div>

          <div className={`flex items-center p-3 rounded-xl ${goals.notebook ? 'bg-white/20' : 'bg-white/10'}`}>
            {goals.notebook ? <CheckCircle2 className="w-5 h-5 mr-2 text-green-300" /> : <Circle className="w-5 h-5 mr-2 text-white/50" />}
            <div>
              <p className={`text-sm font-medium ${goals.notebook ? 'line-through text-white/70' : ''}`}>Study a Notebook</p>
              <p className="text-xs text-indigo-200">Create or review notes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <button
          onClick={() => navigate('/lms/mock-tests')}
          className="flex flex-col items-center p-4 bg-white border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-md transition-all group"
        >
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-2 group-hover:bg-purple-200 transition-colors">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <span className="text-xs font-medium text-gray-700">Generate Test</span>
        </button>

        <button
          onClick={() => navigate('/chat')}
          className="flex flex-col items-center p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all group"
        >
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-2 group-hover:bg-blue-200 transition-colors">
            <MessageCircle className="w-5 h-5 text-blue-600" />
          </div>
          <span className="text-xs font-medium text-gray-700">Ask Doubts</span>
        </button>

        <button
          onClick={() => navigate('/notebooks')}
          className="flex flex-col items-center p-4 bg-white border border-gray-200 rounded-xl hover:border-teal-300 hover:shadow-md transition-all group"
        >
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center mb-2 group-hover:bg-teal-200 transition-colors">
            <FileText className="w-5 h-5 text-teal-600" />
          </div>
          <span className="text-xs font-medium text-gray-700">AI Notebooks</span>
        </button>

        <button
          onClick={() => navigate('/lms/courses')}
          className="flex flex-col items-center p-4 bg-white border border-gray-200 rounded-xl hover:border-green-300 hover:shadow-md transition-all group"
        >
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-2 group-hover:bg-green-200 transition-colors">
            <BookOpen className="w-5 h-5 text-green-600" />
          </div>
          <span className="text-xs font-medium text-gray-700">Study Materials</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="p-1.5 bg-purple-100 rounded-lg">
              <ClipboardCheck className="w-4 h-4 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{attempts.length}</p>
          <p className="text-xs text-gray-500">Mock Tests Taken</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="p-1.5 bg-blue-100 rounded-lg">
              <TrendingUp className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{avgScore !== null ? `${avgScore}%` : '--'}</p>
          <p className="text-xs text-gray-500">Average Score</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="p-1.5 bg-amber-100 rounded-lg">
              <Trophy className="w-4 h-4 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{bestAttempt ? `${Math.round(bestAttempt.pct)}%` : '--'}</p>
          <p className="text-xs text-gray-500">Best Score</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="p-1.5 bg-green-100 rounded-lg">
              <Target className="w-4 h-4 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalQuestionsPracticed}</p>
          <p className="text-xs text-gray-500">Questions Practiced</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Mock Test Attempts */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Recent Mock Tests</h2>
            <Link to="/lms/mock-tests" className="text-xs text-primary-600 hover:underline flex items-center">
              View All <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </div>
          {attempts.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {attempts.slice(0, 5).map((attempt) => {
                const pct = attempt.status === 'graded' && attempt.max_score > 0
                  ? Math.round((attempt.score / attempt.max_score) * 100)
                  : null;
                return (
                  <div
                    key={attempt.id}
                    onClick={() => navigate(`/lms/assessments/${attempt.assessment_id}`)}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{attempt.assessment_title}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(attempt.started_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        {' - '}
                        <span className={
                          attempt.status === 'graded' ? 'text-green-600' :
                          attempt.status === 'in_progress' ? 'text-amber-600' : 'text-blue-600'
                        }>
                          {attempt.status === 'graded' ? 'Graded' : attempt.status === 'in_progress' ? 'In Progress' : 'Submitted'}
                        </span>
                      </p>
                    </div>
                    {pct !== null && (
                      <div className={`ml-3 px-2.5 py-1 rounded-lg text-sm font-bold ${
                        pct >= 70 ? 'bg-green-100 text-green-700' :
                        pct >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {pct}%
                      </div>
                    )}
                    {attempt.status === 'in_progress' && (
                      <div className="ml-3 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700">
                        Resume
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <ClipboardCheck className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No mock tests yet</p>
              <button
                onClick={() => navigate('/lms/mock-tests')}
                className="mt-2 text-xs text-primary-600 hover:underline"
              >
                Generate your first test
              </button>
            </div>
          )}
        </div>

        {/* Recent Notebooks */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Recent Notebooks</h2>
            <Link to="/notebooks" className="text-xs text-primary-600 hover:underline flex items-center">
              View All <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </div>
          {notebooks.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {notebooks.slice(0, 5).map((nb) => (
                <div
                  key={nb.id}
                  onClick={() => navigate(`/notebooks`)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors flex items-center"
                >
                  <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <FileText className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{nb.title || 'Untitled'}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(nb.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      {nb.char_count ? ` - ${Math.round(nb.char_count / 1000)}k chars` : ''}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No notebooks yet</p>
              <button
                onClick={() => navigate('/notebooks')}
                className="mt-2 text-xs text-primary-600 hover:underline"
              >
                Upload your first notes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Day Planner */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 text-indigo-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-700">Day Planner</h2>
          </div>
          <span className="text-xs text-gray-400">
            {plannerTasks.filter(t => t.done).length}/{plannerTasks.length} done
          </span>
        </div>

        {/* Add task */}
        <div className="p-3 border-b bg-gray-50">
          <div className="flex gap-2">
            <input
              type="text"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTask()}
              placeholder="Add a study task..."
              className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
            />
            <input
              type="time"
              value={newTaskTime}
              onChange={(e) => setNewTaskTime(e.target.value)}
              className="w-28 text-sm px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <button
              onClick={addTask}
              disabled={!newTask.trim()}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Task list */}
        {plannerTasks.length > 0 ? (
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {plannerTasks
              .sort((a, b) => {
                if (a.done !== b.done) return a.done ? 1 : -1;
                if (a.time && b.time) return a.time.localeCompare(b.time);
                if (a.time) return -1;
                if (b.time) return 1;
                return 0;
              })
              .map((task) => (
              <div
                key={task.id}
                className={`flex items-center px-4 py-2.5 group hover:bg-gray-50 transition-colors ${task.done ? 'bg-gray-50/50' : ''}`}
              >
                <button onClick={() => toggleTask(task.id)} className="mr-3 flex-shrink-0">
                  {task.done
                    ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                    : <Circle className="w-5 h-5 text-gray-300 hover:text-indigo-400" />
                  }
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${task.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                    {task.text}
                  </p>
                </div>
                {task.time && (
                  <span className={`text-xs mr-3 flex items-center ${task.done ? 'text-gray-300' : 'text-gray-500'}`}>
                    <Clock className="w-3 h-3 mr-1" />
                    {task.time}
                  </span>
                )}
                <button
                  onClick={() => deleteTask(task.id)}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-400 text-sm">
            No tasks yet. Plan your study day above!
          </div>
        )}

        {/* Progress bar */}
        {plannerTasks.length > 0 && (
          <div className="px-4 py-2 border-t bg-gray-50">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-indigo-500 rounded-full h-1.5 transition-all duration-500"
                style={{ width: `${plannerTasks.length > 0 ? (plannerTasks.filter(t => t.done).length / plannerTasks.length) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Activity Summary */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Overall Activity</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{progress?.totalQuestions || 0}</p>
            <p className="text-xs text-gray-500">Doubts Asked</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{progress?.conversationCount || 0}</p>
            <p className="text-xs text-gray-500">Conversations</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{progress?.currentStreak || 0}</p>
            <p className="text-xs text-gray-500">Day Streak</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{notebooks.length}</p>
            <p className="text-xs text-gray-500">Notebooks</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentDashboard;
